import { ShoppingCartOutlined } from '@ant-design/icons';
import { css } from '@linaria/core';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Flex, Popover, Typography } from 'antd';
import { useEffect, useState } from 'react';

import { subscribeToCartItemsAdded } from '@lib/events/cart';
import { useCart } from '@lib/providers/cart-provider';
import { ROUTE_PATHS } from '@modules/route-paths';

const styles = {
  content: css`
    min-width: min(220px, calc(100vw - 32px));
    max-width: calc(100vw - 32px);
  `,
  badge: css`
    .ant-badge-count {
      box-shadow: 0 0 0 1px var(--ant-color-bg-container);
    }
  `,
};

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  currency: 'BRL',
  style: 'currency',
});

interface CartButtonProps {
  accountId: string;
}

const CartButton = ({ accountId }: CartButtonProps) => {
  const { distinctItemCount, selectCart, totalPrice, totalQuantity } =
    useCart();
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    selectCart(accountId);
  }, [accountId, selectCart]);

  useEffect(() => subscribeToCartItemsAdded(() => setPopoverOpen(true)), []);

  const content = (
    <Flex className={styles.content} gap={8} vertical>
      <Typography.Text strong>Carrinho atualizado</Typography.Text>
      <Typography.Text type="secondary">
        {totalQuantity} {totalQuantity === 1 ? 'item' : 'itens'} ·{' '}
        {currencyFormatter.format(totalPrice)}
      </Typography.Text>
      <Link to={ROUTE_PATHS.CART} onClick={() => setPopoverOpen(false)}>
        Ver carrinho
      </Link>
    </Flex>
  );

  return (
    <Popover
      content={content}
      open={popoverOpen}
      placement="bottom"
      trigger="click"
      onOpenChange={setPopoverOpen}
    >
      <Badge
        className={styles.badge}
        count={totalQuantity}
        overflowCount={99}
        size="small"
      >
        <Button
          aria-label={`Carrinho com ${distinctItemCount} itens diferentes`}
          icon={<ShoppingCartOutlined />}
          shape="circle"
        />
      </Badge>
    </Popover>
  );
};

export default CartButton;
